import { useEffect, useState, useMemo, useCallback } from "react";
import { Card, CardBody, Col, Container, CardHeader, Row } from "reactstrap";
import {
  RoleDtoCodeEnum,
  UserDto,
  UserDtoStatusEnum,
} from "generated/apiUsers";
import { Link } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import DigitalTableContainer from "../../Components/Common/DigitalTableContainer";
import _ from "lodash";
import DeleteModal from "../../Components/Common/DeleteModal";
import UserFilter from "./UserFilter";
import UserModal from "./UserModal";

// Export Modal
import ExportCSVModal from "../../Components/Common/ExportCSVModal";
import { exportUsers, fetchUsers } from "api/queries/user";

import Loader from "../../Components/Common/Loader";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import moment from "moment";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SortingState } from "@tanstack/react-table";
import { deleteUser } from "api/mutators/user";
import { useTranslation } from "react-i18next";
import './User.scss'
import { getLoggedinUser } from "helpers/api_helper";

const UserTable = () => {
  const { t } = useTranslation("translation", { keyPrefix: "USERS" });
  const queryClient = useQueryClient();
  const authUser: any = getLoggedinUser();

  const [user, setUser] = useState<UserDto | null>(null);
  const [modal, setModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);

  const [search, setSearch] = useState<string>("");

  const [pageIndex, setPageIndex] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState<SortingState>([]);
  const [orderBy, setOrderBy] = useState<any[]>([]);

  const schema = yup.object().shape({
    search: yup.string().required("Search is required"),
    status: yup.object().shape({
      value: yup
        .mixed<UserDtoStatusEnum>()
        .oneOf(Object.values(UserDtoStatusEnum)),
      label: yup.string().required("status is required"),
    }),
    role: yup.object().shape({
      value: yup.mixed<RoleDtoCodeEnum>().oneOf(Object.values(RoleDtoCodeEnum)),
      label: yup.string().required("role is required"),
    }),
  });

  const defaultValues = {
    search: "",
    status: { label: "All", value: undefined },
    role: { label: "All", value: undefined },
  };

  // ** Form
  const methods = useForm({
    defaultValues,
    mode: "onChange",
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    const mySort = sorting.map((item) => ({
      [item.id]: item.desc ? "desc" : "asc",
    }));
    setOrderBy(mySort);
  }, [sorting]);

  // ** Mutation - Impostata la chiamata API
  const mutation = useMutation({
    mutationFn: deleteUser,
    onError: (error: any) => {
      queryClient.invalidateQueries({ queryKey: ["fetchUsers"] });
      queryClient.invalidateQueries({ queryKey: ["fetchUserInfo"] });
      queryClient.invalidateQueries({ queryKey: ["exportUsers"] });
      toast.error("Error! Users not deleted", { autoClose: 3000 });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["fetchUsers"] });
      queryClient.invalidateQueries({ queryKey: ["fetchUserInfo"] });
      queryClient.invalidateQueries({ queryKey: ["exportUsers"] });
      toast.success("Users Deleteds Successfully", { autoClose: 3000 });
    },
  });

  const { isPending, data, error } = useQuery({
    queryKey: fetchUsers.queryKey([
      search,
      { ...methods.watch(), pageIndex, pageSize, orderBy },
    ]),
    queryFn: () =>
      fetchUsers.queryFn({
        orderBy,
        take: pageSize,
        skip: pageIndex * pageSize,
        filter: {
          search,
          status: methods.watch().status.value as UserDtoStatusEnum,
          role: methods.watch().role.value as RoleDtoCodeEnum,
        },
      }),
  });

  const { isPending: isPendingExport, data: dataExport } = useQuery({
    queryKey: exportUsers.queryKey([search, { ...methods.watch(), orderBy }]),
    queryFn: () =>
      exportUsers.queryFn({
        orderBy,
        take: 5000,
        skip: 0,
        filter: {
          search,
          status: methods.watch().status.value as UserDtoStatusEnum,
          role: methods.watch().role.value as RoleDtoCodeEnum,
        },
      }),
  });

  const debounceFunction = useCallback(
    _.debounce((value) => {
      setSearch(value);
      // Puoi aggiungere qui la tua logica di validazione o chiamata API
    }, 500), // 500 ms debounce
    []
  );

  const onClickDelete = (user: any) => {
    setUser(user);
    setDeleteModal(true);
  };

  const handleDeleteUser = () => { 
    if (user) {
      mutation.mutate( user.id);
      setDeleteModal(false);
    }
  };

  const toggle = useCallback(() => {
    if (modal) {
      setModal(false);
      setUser(null);
    } else {
      setModal(true);
    }
  }, [modal]);


  // Column
  const columns = [
    {
      header: 'id',
      id: "#",
      accessorKey: "id",
      enableColumnFilter: false,
      enableSorting: false,
    },
    {
      header: `${t("DETAILS:TABLE:NAME")}`,
      accessorKey: "name",
      enableColumnFilter: false,
    },
    {
      header: `${t("DETAILS:TABLE:SURNAME")}`,
      accessorKey: "surname",
      enableColumnFilter: false,
    },
    {
      header: `${t("DETAILS:TABLE:EMAIL")}`,
      accessorKey: "email",
      enableColumnFilter: false,
    },
    {
      header: `${t("DETAILS:TABLE:TELEPHONE")}`,
      accessorKey: "telephone",
      enableColumnFilter: false,
    },
    {
      header: `${t("DETAILS:TABLE:CITY")}`,
      accessorKey: "city",
      enableColumnFilter: false,
    },
    {
      header: `${t("DETAILS:TABLE:ROLE")}`,
      accessorKey: "mainRole",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (cell: any) => {
        switch (cell.getValue().code) {
          case RoleDtoCodeEnum.Manager:
            return (
              <span className="badge text-uppercase bg-warning-subtle text-warning">
                {" "}
                {cell.getValue().code}{" "}
              </span>
            );
          case RoleDtoCodeEnum.Superadmin:
            return (
              <span className="badge text-uppercase bg-danger-subtle text-danger">
                {" "}
                {cell.getValue().code}{" "}
              </span>
            );
          case RoleDtoCodeEnum.CaregiverProfessional:
            return (
              <span className="badge text-uppercase bg-success-subtle text-success">
                {" "}
                {cell.getValue().code}{" "}
              </span>
            );

          case RoleDtoCodeEnum.CaregiverUser:
            return (
              <span className="badge text-uppercase bg-info-subtle text-info">
                {" "}
                {cell.getValue().code}{" "}
              </span>
            );
        }
      },
    },
    {
      header: `${t("DETAILS:TABLE:DATA_CREATION")}`,
      accessorKey: "createdAt",
      enableColumnFilter: false,
      cell: (cell: any) => (
        <>
          {handleValidDate(cell.getValue())},
          <small className="text-muted">
            {" "}
            {handleValidTime(cell.getValue())}
          </small>
        </>
      ),
    },
    {
      header: `${t("DETAILS:TABLE:STATUS")}`,
      accessorKey: "status",
      enableColumnFilter: false,
      enableSorting: false,
      cell: (cell: any) => {
        switch (cell.getValue()) {
          case UserDtoStatusEnum.Active:
            return (
              <span className="badge text-uppercase bg-success-subtle text-success">
                {" "}
                {cell.getValue()}{" "}
              </span>
            );
          case UserDtoStatusEnum.Inactive:
            return (
              <span className="badge text-uppercase bg-danger-subtle text-danger">
                {" "}
                {cell.getValue()}{" "}
              </span>
            );
        }
      },
    },
    {
      header: `${t("DETAILS:TABLE:ACTIONS")}`,
      cell: (cellProps: any) => {

        console.log("authUser.role", authUser.role);
        console.log("cellProps.row.original?.mainRole?.code", cellProps.row.original?.mainRole?.code);
        console.log("Test 1", authUser.role === RoleDtoCodeEnum.Superadmin && cellProps.row.original?.mainRole?.code !== RoleDtoCodeEnum.Superadmin);
        return  (
          <ul className="list-inline hstack gap-2 mb-0">
            { ((authUser.role === RoleDtoCodeEnum.Superadmin && cellProps.row.original?.mainRole?.code !== RoleDtoCodeEnum.Superadmin)) && (
            <li className="list-inline-item">
              <Link
                to="#"
                className="text-danger d-inline-block remove-item-btn"
                onClick={() => {
                  const userData = cellProps.row.original;
                  onClickDelete(userData);
                }}
              >
                <i className="ri-delete-bin-5-fill fs-16"></i>
              </Link>
            </li>
            )}
          </ul> 
              
        );
      },
    },
  ];

  const handleValidDate = (date: any) => {
    const date1 = moment(new Date(date)).format("DD MMM Y");
    return date1;
  };

  const handleValidTime = (time: any) => {
    let orario = moment(new Date(time)).format("HH:mm");
    return orario;
  };

  // Export Modal
  const [isExportCSV, setIsExportCSV] = useState<boolean>(false);

  document.title = "Users | AICare";
  return (
    <div className="page-content">
      <FormProvider {...methods}>
        <UserModal modal={modal} toggle={toggle} setModal={setModal} />

        <ExportCSVModal
          show={isExportCSV}
          onCloseClick={() => setIsExportCSV(false)}
          data={!isPendingExport && dataExport?.data}
        />
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDeleteUser}
          onCloseClick={() => setDeleteModal(false)}
        />

        <Container fluid>
          <BreadCrumb title={t("LAYOUT:TITLE")} pageTitle="Menù" />
          <Row>
            <Col lg={12}>
              <Card id="userList">
                <CardHeader className="card-header border-0">
                  <Row className="align-items-center gy-3">
                    <div className="col-sm">
                      <h5 className="card-title mb-0"> {t("LAYOUT:TITLE")}</h5>
                    </div>
                    <div className="col-sm-auto">
                      <div className="d-flex gap-1 flex-wrap">
                        {/* <button
                          type="button"
                          className="btn btn-soft-danger add-btn"
                          id="create-btn"
                          onClick={() => {
                            toggle();
                          }}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          {t("LAYOUT:CREATE")}
                        </button>{" "} */}
                        <button
                          type="button"
                          className="btn btn-soft-danger"
                          onClick={() => setIsExportCSV(true)}
                        >
                          <i className="ri-file-download-line align-bottom me-1"></i>{" "}
                          {t("LAYOUT:EXPORT")}
                        </button>{" "}
                      </div>
                    </div>
                  </Row>
                </CardHeader>

                <UserFilter debounceFn={debounceFunction} />
                <CardBody className="pt-0">
                  <div>
                    {!isPending && data?.data ? (
                      <DigitalTableContainer
                        columns={columns}
                        data={data?.data || []}
                        numberOfRows={data?.count}
                        setPageIndex={setPageIndex}
                        setPageSize={setPageSize}
                        setSorting={setSorting}
                        pageSize={pageSize}
                        pageIndex={pageIndex}
                        sorting={sorting}
                        divClass="table-responsive table-card mb-1 mt-0"
                        tableClass="align-middle table-nowrap"
                        theadClass="table-light text-muted text-uppercase"
                      />
                    ) : (
                      <Loader error={error} />
                    )}
                  </div>
                  <ToastContainer closeButton={false} limit={1} />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </FormProvider>
    </div>
  );
};

export default UserTable;
